import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import MessageBubble from './MessageBubble';
import ToolCallCard from './ToolCallCard';
import ToolCallGroupCard from './ToolCallGroupCard';
import SystemEvent from './SystemEvent';
import './MessageStream.scss';

// Tool-result rows are rendered inside their parent ToolCallCard, not
// inline in the stream. This keeps the visual nesting clean — agents
// can produce N tool_call/tool_result pairs per turn without flooding
// the stream with raw JSON outputs.
//
// A run of consecutive calls to the SAME tool (e.g. four "Discover creators") is then collapsed
// into one `tool_call_group` so a repeated step reads as a single card with a count instead of
// sprawling the stream. Any other row — a message, a system_event, a different tool — breaks the
// run, and a lone call unwraps back to a plain tool_call (no group chrome).
export const buildRenderableRows = messages => {
  const byParent = _.groupBy(messages.filter(m => m && m.role === 'tool_result'), 'parentAgentMessage_id');
  const rows = messages
    .filter(m => m && m.role !== 'tool_result')
    .map(m => ({
      ...m,
      toolResults: m.role === 'tool_call' ? (byParent[m.id] || []) : []
    }));

  const grouped = [];
  rows.forEach(row => {
    const toolName = row.role === 'tool_call' ? _.get(row, 'metadata.toolName') : null;
    const prev = grouped[grouped.length - 1];
    if (toolName && prev && prev.role === 'tool_call_group' && prev.toolName === toolName) {
      prev.calls.push(row);
    } else if (row.role === 'tool_call') {
      grouped.push({ role: 'tool_call_group', id: row.id, toolName, calls: [row] });
    } else {
      grouped.push(row);
    }
  });
  return grouped.map(r => (r.role === 'tool_call_group' && r.calls.length === 1 ? r.calls[0] : r));
};

// Scroll model:
//
//   - A user is "pinned" to the bottom when within PIN_THRESHOLD_PX of
//     scrollHeight. We follow content growth only while pinned, so a
//     reader scrolled up into history isn't yanked.
//   - ANY content height change (new message, streaming bubble,
//     ToolCallCard expand, image load) is caught by a ResizeObserver on
//     the content wrapper — a count-based effect would miss every
//     non-message growth.
//   - Force-scroll bypasses the pin check; used for explicit user
//     actions (composer send) and for container reveals.
//   - Jump-to-bottom button shows when the user is more than one
//     viewport from the bottom — far enough they almost certainly
//     scrolled deliberately.
const PIN_THRESHOLD_PX  = 80;
const JUMP_THRESHOLD_PX = el => el.clientHeight;

// App couplings are inverted to slots and callbacks: `renderEmptyState` owns the zero-message
// landing, `renderAfterMessage(message)` renders trailing row content,
// `renderToolResultCard(result, call)` the rich tool-result treatments, and the
// bubble-level inversions (`onRetryMessage`, `renderLink`, `renderMessageMeta`,
// `renderMessageActions`, lifecycle-chip props) are threaded through unchanged.
const MessageStream = ({
  activeThread,
  messages,
  isWorking,
  fetchInfo,
  renderEmptyState,
  renderAfterMessage,
  renderToolResultCard,
  onRetryMessage,
  renderLink,
  renderMessageMeta,
  renderMessageActions,
  lifecycleEventsVisible,
  onReplayLifecycleEvent
}) => {
  const scrollerRef = useRef(null);
  const contentRef  = useRef(null);
  const pinnedRef   = useRef(true);

  const [isScrolledUp, setIsScrolledUp] = useState(false);

  const scrollToBottom = useCallback((behavior = 'auto') => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const handleJumpToBottom = useCallback(() => scrollToBottom('smooth'), [scrollToBottom]);

  // Recompute "pinned" + jump-button visibility from the live scroll geometry. Called on scroll
  // AND whenever the stream/container resizes, so a transient measurement during a
  // container morph can't leave the jump button stuck on.
  const syncScrollMetrics = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinnedRef.current = distance < PIN_THRESHOLD_PX;
    setIsScrolledUp(distance > JUMP_THRESHOLD_PX(el));
  }, []);

  // Scroll listener: keep pinnedRef in sync + drive the jump-to-bottom button visibility.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    syncScrollMetrics();
    el.addEventListener('scroll', syncScrollMetrics, { passive: true });
    return () => el.removeEventListener('scroll', syncScrollMetrics);
  }, [syncScrollMetrics]);

  // Content-resize observer: any height change in the rendered stream
  // (messages added, bubbles streaming, ToolCall toggling, image loads)
  // triggers a follow-if-pinned check. This is the
  // single source of truth for content-driven autoscroll.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const content  = contentRef.current;
    if (!scroller || !content || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (pinnedRef.current) scrollToBottom('auto');
      syncScrollMetrics();
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [scrollToBottom, syncScrollMetrics]);

  // Container-resize observer: when the scroller itself grows (surrounding
  // chrome reveals more of it), force-follow so the reader lands on the
  // latest regardless of the now-stale scroll position from when the
  // container was smaller.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || typeof ResizeObserver === 'undefined') return undefined;
    let lastClientHeight = scroller.clientHeight;
    const observer = new ResizeObserver(() => {
      const current = scroller.clientHeight;
      if (current > lastClientHeight + 8) scrollToBottom('auto');
      lastClientHeight = current;
      syncScrollMetrics();
    });
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [scrollToBottom, syncScrollMetrics]);

  // Explicit user-action escape hatch (e.g. composer send). Forces a
  // smooth scroll regardless of the pin state — the user just took an
  // action and expects to see its outcome.
  useEffect(() => {
    const onForceScroll = () => {
      pinnedRef.current = true;
      scrollToBottom('smooth');
    };
    window.addEventListener('autopilot:scroll-to-latest', onForceScroll);
    return () => window.removeEventListener('autopilot:scroll-to-latest', onForceScroll);
  }, [scrollToBottom]);

  const rows = buildRenderableRows(messages);
  const showEmpty = !fetchInfo.isFetching && rows.length === 0 && !isWorking;

  return (
    <div className='ap-message-stream-wrap'>
    <div className='ap-message-stream' ref={scrollerRef} role='log' aria-live='polite' aria-atomic='false'>
      <div className='ap-message-stream-content' ref={contentRef}>
      {showEmpty && renderEmptyState ? renderEmptyState() : null}

      {rows.map(row => {
        if (row.role === 'system_event') {
          return (
            <SystemEvent
              key={row.id || row.metadata?.clientMessageId}
              text={row.message || _.get(row, 'metadata.text') || ''}
            />
          );
        }
        if (row.role === 'tool_call_group') {
          return (
            <ToolCallGroupCard
              key={row.id}
              group={row}
              thread={activeThread}
              renderResultCard={renderToolResultCard}
              lifecycleEventsVisible={lifecycleEventsVisible}
              onReplayLifecycleEvent={onReplayLifecycleEvent}
            />
          );
        }
        if (row.role === 'tool_call') {
          return (
            <ToolCallCard
              key={row.id}
              call={row}
              results={row.toolResults}
              thread={activeThread}
              renderResultCard={renderToolResultCard}
              lifecycleEventsVisible={lifecycleEventsVisible}
              onReplayLifecycleEvent={onReplayLifecycleEvent}
            />
          );
        }
        return (
          <React.Fragment key={row.id || row.metadata?.clientMessageId}>
            <MessageBubble
              message={row}
              AgentThread_id={activeThread && activeThread.id}
              onRetry={onRetryMessage}
              renderLink={renderLink}
              renderMeta={renderMessageMeta}
              renderActions={renderMessageActions}
              lifecycleEventsVisible={lifecycleEventsVisible}
              onReplayLifecycleEvent={onReplayLifecycleEvent}
            />
            {renderAfterMessage ? renderAfterMessage(row) : null}
          </React.Fragment>
        );
      })}
      </div>
    </div>
      {isScrolledUp && rows.length > 0 && (
        <button
          type='button'
          className='ap-jump-to-bottom'
          onClick={handleJumpToBottom}
          aria-label='Scroll to latest message'
        >
          <svg viewBox='0 0 16 16' aria-hidden='true' focusable='false'>
            <path d='M3.5 6.5l4.5 4.5 4.5-4.5' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
        </button>
      )}
    </div>
  );
};

MessageStream.propTypes = {
  activeThread: PropTypes.object,
  messages:     PropTypes.array.isRequired,
  isWorking:    PropTypes.bool.isRequired,
  fetchInfo:    PropTypes.object.isRequired,
  renderEmptyState:     PropTypes.func,
  renderAfterMessage:   PropTypes.func,
  renderToolResultCard: PropTypes.func,
  onRetryMessage:       PropTypes.func,
  renderLink:           PropTypes.func,
  renderMessageMeta:    PropTypes.func,
  renderMessageActions: PropTypes.func,
  lifecycleEventsVisible: PropTypes.bool,
  onReplayLifecycleEvent: PropTypes.func
};

export default MessageStream;
